/**
 * Stammdaten (/stammdaten) — Verwaltungsbereich für Kataloge/Lookup-Tabellen
 * (Muster rmnc-personal /admin/stammdaten): KVP-Status, Prüfungsarten,
 * Wartungsarten. Sichtbarkeit über die Sidebar rollenbasiert; durchgesetzt
 * werden die Rechte serverseitig (Status: admin — Arten: technik_schreiben,
 * Löschen: technik_loeschen).
 */
import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createKvpStatus,
  createPruefungsart,
  createWartungsart,
  deletePruefungsart,
  deleteWartungsart,
  fetchKvpStatus,
  fetchPruefungsarten,
  fetchWartungsarten,
  updateKvpStatus,
  updatePruefungsart,
  updateWartungsart,
  type KvpStatus,
  type KvpStatusEingabe,
  type Pruefungsart,
  type Wartungsart,
} from "@/lib/api"
import { Badge, Button, Input, Select, Textarea } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SHEET_WIDTH,
} from "@/components/Sheet"
import { useToast, fehlerNachricht } from "@/lib/toast"

type Tab = "kvp-status" | "pruefungsarten" | "wartungsarten"

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "kvp-status", label: "KVP-Status" },
  { key: "pruefungsarten", label: "Prüfungsarten" },
  { key: "wartungsarten", label: "Wartungsarten" },
]

const KATEGORIE_LABEL: Record<KvpStatus["kategorie"], string> = {
  offen: "offen",
  erledigt: "erledigt",
  verworfen: "verworfen",
}

const KATEGORIE_TONE: Record<KvpStatus["kategorie"], "orange" | "green" | "red"> = {
  offen: "orange",
  erledigt: "green",
  verworfen: "red",
}

export function StammdatenPage() {
  const [tab, setTab] = useState<Tab>("kvp-status")

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Stammdaten</h1>
        <p className="mt-1 text-sm text-slate-600">
          Kataloge der Technik-Module — Änderungen wirken sofort in den Auswahlfeldern und der
          Fristen-/Status-Logik.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-1" role="tablist" aria-label="Stammdaten-Bereiche">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
              tab === t.key
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "kvp-status" && <KvpStatusTab />}
      {tab === "pruefungsarten" && <PruefungsartenTab />}
      {tab === "wartungsarten" && <WartungsartenTab />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: KVP-Status (05-H) — Kategorie steuert die Status-Ableitung der Ebenen
// ---------------------------------------------------------------------------

interface StatusForm {
  name: string
  kategorie: KvpStatus["kategorie"]
  farbe: string
  reihenfolge: string
  aktiv: boolean
}

const STATUS_LEER: StatusForm = {
  name: "",
  kategorie: "offen",
  farbe: "#e2e8f0",
  reihenfolge: "",
  aktiv: true,
}

function KvpStatusTab() {
  const queryClient = useQueryClient()
  const [sheet, setSheet] = useState<"neu" | KvpStatus | null>(null)
  const { data: statusListe } = useQuery({ queryKey: ["kvp-status"], queryFn: () => fetchKvpStatus() })

  const spalten = useMemo<ColumnDef<KvpStatus>[]>(
    () => [
      {
        id: "reihenfolge",
        header: "Reihenfolge",
        accessorFn: (s) => s.reihenfolge ?? 999,
        cell: ({ row }) => (
          <span className="tabular-nums text-slate-500">{row.original.reihenfolge ?? "—"}</span>
        ),
        meta: { headClassName: "w-28" },
      },
      {
        id: "name",
        header: "Name",
        accessorFn: (s) => s.name,
        cell: ({ row }) => <span className="font-medium text-slate-900">{row.original.name}</span>,
      },
      {
        id: "kategorie",
        header: "Kategorie",
        accessorFn: (s) => s.kategorie,
        cell: ({ row }) => (
          <Badge tone={KATEGORIE_TONE[row.original.kategorie]}>
            {KATEGORIE_LABEL[row.original.kategorie]}
          </Badge>
        ),
      },
      {
        id: "farbe",
        header: "Farbe",
        accessorFn: (s) => s.farbe ?? "",
        cell: ({ row }) =>
          row.original.farbe ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block size-4 rounded-full border border-slate-200"
                style={{ backgroundColor: row.original.farbe }}
                aria-hidden="true"
              />
              <span className="font-mono text-xs text-slate-500">{row.original.farbe}</span>
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      {
        id: "aktiv",
        header: "Aktiv",
        accessorFn: (s) => (s.aktiv ? "ja" : "nein"),
        cell: ({ row }) =>
          row.original.aktiv ? <Badge tone="green">ja</Badge> : <Badge tone="slate">nein</Badge>,
        meta: { headClassName: "w-20" },
      },
    ],
    [],
  )

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Die <strong>Kategorie</strong> steuert die Status-Ableitung im KVP: <em>offen</em> hält
          Teilprojekte in Arbeit, <em>erledigt</em> zählt als Erfolg, <em>verworfen</em>{" "}
          (abgelehnt/abgebrochen) blockiert den Abschluss nicht. Löschen ist bewusst nicht
          vorgesehen — nicht mehr genutzte Status auf „inaktiv" stellen.
        </p>
        <Button type="button" className="shrink-0" onClick={() => setSheet("neu")}>
          + Neuer Status
        </Button>
      </div>
      <DataGrid
        columns={spalten}
        data={statusListe ?? []}
        storageKey="stammdaten-kvp-status"
        initialSorting={[{ id: "reihenfolge", desc: false }]}
        emptyMessage="Noch keine Status konfiguriert."
        onRowClick={(s) => setSheet(s)}
      />
      <StatusSheet
        zustand={sheet}
        onOpenChange={(open) => !open && setSheet(null)}
        onGespeichert={() => {
          queryClient.invalidateQueries({ queryKey: ["kvp-status"] })
          // Kategorien beeinflussen die abgeleiteten Ebenen-Status
          queryClient.invalidateQueries({ queryKey: ["kvp-projekte"] })
          queryClient.invalidateQueries({ queryKey: ["kvp-teilprojekte"] })
        }}
      />
    </section>
  )
}

function StatusSheet({
  zustand,
  onOpenChange,
  onGespeichert,
}: {
  zustand: "neu" | KvpStatus | null
  onOpenChange: (open: boolean) => void
  onGespeichert: () => void
}) {
  const istNeu = zustand === "neu"
  const status = istNeu || zustand === null ? null : zustand
  const toast = useToast()
  const [form, setForm] = useState<StatusForm>(STATUS_LEER)

  useEffect(() => {
    if (zustand === "neu") setForm(STATUS_LEER)
    else if (zustand)
      setForm({
        name: zustand.name,
        kategorie: zustand.kategorie,
        farbe: zustand.farbe ?? "#e2e8f0",
        reihenfolge: zustand.reihenfolge?.toString() ?? "",
        aktiv: zustand.aktiv,
      })
  }, [zustand])

  const speichern = useMutation({
    mutationFn: () => {
      const payload: KvpStatusEingabe = {
        name: form.name.trim(),
        kategorie: form.kategorie,
        farbe: form.farbe || null,
        icon: status?.icon ?? null,
        reihenfolge: form.reihenfolge ? Number(form.reihenfolge) : null,
        aktiv: form.aktiv,
      }
      return istNeu ? createKvpStatus(payload) : updateKvpStatus(status!.id, payload)
    },
    onSuccess: () => {
      toast.success(istNeu ? "Status angelegt." : "Status gespeichert.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Status konnte nicht gespeichert werden.")),
  })

  return (
    <Sheet open={zustand !== null} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="stammdaten-status-form" defaultWidth={SHEET_WIDTH.md}>
        <SheetHeader>
          <SheetTitle>{istNeu ? "Neuer Status" : `Status „${status?.name}“`}</SheetTitle>
          <SheetDescription>
            Aufgaben-Statusliste (05-H). Schreiben erfordert Admin-Rechte.
          </SheetDescription>
        </SheetHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (form.name.trim()) speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="status-name" className="block text-sm text-slate-700">
              Name <span className="text-red-600">*</span>
            </label>
            <Input
              id="status-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="status-kategorie" className="block text-sm text-slate-700">
              Kategorie <span className="text-red-600">*</span>
            </label>
            <Select
              id="status-kategorie"
              value={form.kategorie}
              onChange={(e) =>
                setForm({ ...form, kategorie: e.target.value as KvpStatus["kategorie"] })
              }
            >
              <option value="offen">offen — Aufgabe wird noch bearbeitet</option>
              <option value="erledigt">erledigt — erfolgreich abgeschlossen</option>
              <option value="verworfen">verworfen — geschlossen ohne Ergebnis</option>
            </Select>
            <p className="text-xs text-slate-500">
              Steuert die Status-Ableitung von Teilprojekten/Projekten. „istOffen“ wird
              automatisch konsistent gehalten.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="status-farbe" className="block text-sm text-slate-700">
                Farbe
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="status-farbe"
                  type="color"
                  value={form.farbe}
                  onChange={(e) => setForm({ ...form, farbe: e.target.value })}
                  className="h-9 w-12 cursor-pointer rounded-md border border-slate-200"
                  aria-label="Statusfarbe wählen"
                />
                <Input
                  value={form.farbe}
                  onChange={(e) => setForm({ ...form, farbe: e.target.value })}
                  className="font-mono text-xs"
                  aria-label="Statusfarbe (Hex)"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="status-reihenfolge" className="block text-sm text-slate-700">
                Reihenfolge
              </label>
              <Input
                id="status-reihenfolge"
                type="number"
                value={form.reihenfolge}
                onChange={(e) => setForm({ ...form, reihenfolge: e.target.value })}
              />
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.aktiv}
              onChange={(e) => setForm({ ...form, aktiv: e.target.checked })}
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            />
            Aktiv (erscheint in den Auswahlfeldern)
          </label>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              {istNeu ? "Status anlegen" : "Speichern"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Tab: Prüfungsarten (02) — Intervall speist die Fristen-Engine (§5)
// ---------------------------------------------------------------------------

type PruefungsartForm = Record<
  | "pruefungsart"
  | "pruefungsintervallJahre"
  | "pruefungNach"
  | "zustaendigeStelle"
  | "geplanteDauerStunden"
  | "anzahlMitarbeiter"
  | "vorbereitungszeitTage"
  | "notizen",
  string
>

const PRUEFUNGSART_LEER: PruefungsartForm = {
  pruefungsart: "",
  pruefungsintervallJahre: "",
  pruefungNach: "",
  zustaendigeStelle: "",
  geplanteDauerStunden: "",
  anzahlMitarbeiter: "",
  vorbereitungszeitTage: "",
  notizen: "",
}

function PruefungsartenTab() {
  const queryClient = useQueryClient()
  const [sheet, setSheet] = useState<"neu" | Pruefungsart | null>(null)
  const { data: arten } = useQuery({ queryKey: ["pruefungsarten"], queryFn: fetchPruefungsarten })

  const spalten = useMemo<ColumnDef<Pruefungsart>[]>(
    () => [
      {
        id: "pruefungsart",
        header: "Prüfungsart",
        accessorFn: (a) => a.pruefungsart,
        cell: ({ row }) => (
          <span className="font-medium text-slate-900">{row.original.pruefungsart}</span>
        ),
      },
      {
        id: "intervall",
        header: "Intervall (Jahre)",
        accessorFn: (a) => a.pruefungsintervallJahre ?? -1,
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.pruefungsintervallJahre ?? "—"}</span>
        ),
      },
      {
        id: "pruefungNach",
        header: "Prüfung nach",
        accessorFn: (a) => a.pruefungNach ?? "",
        cell: ({ row }) => row.original.pruefungNach ?? "—",
      },
      {
        id: "stelle",
        header: "Zuständige Stelle",
        accessorFn: (a) => a.zustaendigeStelle ?? "",
        cell: ({ row }) => row.original.zustaendigeStelle ?? "—",
      },
      {
        id: "dauer",
        header: "Dauer (h)",
        accessorFn: (a) => a.geplanteDauerStunden ?? -1,
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.geplanteDauerStunden ?? "—"}</span>
        ),
      },
      {
        id: "vorbereitung",
        header: "Vorbereitung (Tage)",
        accessorFn: (a) => a.vorbereitungszeitTage ?? -1,
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.vorbereitungszeitTage ?? "—"}</span>
        ),
      },
    ],
    [],
  )

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Das Prüfungsintervall speist die Fristen-Engine: Ablaufdatum = Prüfungsdatum + Intervall
          (§5). Löschen ist nur möglich, solange keine Prüfungen auf die Art verweisen.
        </p>
        <Button type="button" className="shrink-0" onClick={() => setSheet("neu")}>
          + Neue Prüfungsart
        </Button>
      </div>
      <DataGrid
        columns={spalten}
        data={arten ?? []}
        storageKey="stammdaten-pruefungsarten"
        emptyMessage="Noch keine Prüfungsarten erfasst."
        onRowClick={(a) => setSheet(a)}
      />
      <PruefungsartSheet
        zustand={sheet}
        onOpenChange={(open) => !open && setSheet(null)}
        onGeaendert={() => queryClient.invalidateQueries({ queryKey: ["pruefungsarten"] })}
      />
    </section>
  )
}

function PruefungsartSheet({
  zustand,
  onOpenChange,
  onGeaendert,
}: {
  zustand: "neu" | Pruefungsart | null
  onOpenChange: (open: boolean) => void
  onGeaendert: () => void
}) {
  const istNeu = zustand === "neu"
  const art = istNeu || zustand === null ? null : zustand
  const toast = useToast()
  const [form, setForm] = useState<PruefungsartForm>(PRUEFUNGSART_LEER)

  useEffect(() => {
    if (zustand === "neu") setForm(PRUEFUNGSART_LEER)
    else if (zustand)
      setForm({
        pruefungsart: zustand.pruefungsart,
        pruefungsintervallJahre: zustand.pruefungsintervallJahre?.toString() ?? "",
        pruefungNach: zustand.pruefungNach ?? "",
        zustaendigeStelle: zustand.zustaendigeStelle ?? "",
        geplanteDauerStunden: zustand.geplanteDauerStunden?.toString() ?? "",
        anzahlMitarbeiter: zustand.anzahlMitarbeiter?.toString() ?? "",
        vorbereitungszeitTage: zustand.vorbereitungszeitTage?.toString() ?? "",
        notizen: zustand.notizen ?? "",
      })
  }, [zustand])

  const zahl = (wert: string): number | null => (wert.trim() === "" ? null : Number(wert))
  const text = (wert: string): string | null => wert.trim() || null

  const speichern = useMutation({
    mutationFn: () => {
      const payload = {
        pruefungsart: form.pruefungsart.trim(),
        pruefungsintervallJahre: zahl(form.pruefungsintervallJahre),
        pruefungNach: text(form.pruefungNach),
        anbieterOrganisationId: art?.anbieterOrganisationId ?? null,
        zustaendigeStelle: text(form.zustaendigeStelle),
        geplanteDauerStunden: zahl(form.geplanteDauerStunden),
        anzahlMitarbeiter: zahl(form.anzahlMitarbeiter),
        vorbereitungszeitTage: zahl(form.vorbereitungszeitTage),
        notizen: text(form.notizen),
      }
      return istNeu ? createPruefungsart(payload) : updatePruefungsart(art!.id, payload)
    },
    onSuccess: () => {
      toast.success(istNeu ? "Prüfungsart angelegt." : "Prüfungsart gespeichert.")
      onGeaendert()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Prüfungsart konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deletePruefungsart(art!.id),
    onSuccess: () => {
      toast.success("Prüfungsart gelöscht.")
      onGeaendert()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(
        fehlerNachricht(err, "Löschen fehlgeschlagen — wird die Art noch von Prüfungen genutzt?"),
      ),
  })

  return (
    <Sheet open={zustand !== null} onOpenChange={onOpenChange}>
      <SheetContent
        className="overflow-y-auto"
        sheetKey="stammdaten-pruefungsart-form"
        defaultWidth={SHEET_WIDTH.md}
      >
        <SheetHeader>
          <SheetTitle>
            {istNeu ? "Neue Prüfungsart" : `Prüfungsart „${art?.pruefungsart}“`}
          </SheetTitle>
          <SheetDescription>
            Katalog der gesetzlichen/technischen Prüfungen (02) — Intervall bestimmt das
            Ablaufdatum.
          </SheetDescription>
        </SheetHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (form.pruefungsart.trim()) speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="pa-name" className="block text-sm text-slate-700">
              Prüfungsart <span className="text-red-600">*</span>
            </label>
            <Input
              id="pa-name"
              required
              value={form.pruefungsart}
              onChange={(e) => setForm({ ...form, pruefungsart: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="pa-intervall" className="block text-sm text-slate-700">
                Intervall (Jahre)
              </label>
              <Input
                id="pa-intervall"
                type="number"
                step="0.5"
                value={form.pruefungsintervallJahre}
                onChange={(e) => setForm({ ...form, pruefungsintervallJahre: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="pa-nach" className="block text-sm text-slate-700">
                Prüfung nach
              </label>
              <Input
                id="pa-nach"
                value={form.pruefungNach}
                onChange={(e) => setForm({ ...form, pruefungNach: e.target.value })}
                placeholder="z. B. BetrSichV §16"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="pa-stelle" className="block text-sm text-slate-700">
              Zuständige Stelle
            </label>
            <Input
              id="pa-stelle"
              value={form.zustaendigeStelle}
              onChange={(e) => setForm({ ...form, zustaendigeStelle: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="pa-dauer" className="block text-sm text-slate-700">
                Dauer (h)
              </label>
              <Input
                id="pa-dauer"
                type="number"
                step="0.5"
                value={form.geplanteDauerStunden}
                onChange={(e) => setForm({ ...form, geplanteDauerStunden: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="pa-ma" className="block text-sm text-slate-700">
                Mitarbeiter
              </label>
              <Input
                id="pa-ma"
                type="number"
                value={form.anzahlMitarbeiter}
                onChange={(e) => setForm({ ...form, anzahlMitarbeiter: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="pa-vorb" className="block text-sm text-slate-700">
                Vorbereitung (Tage)
              </label>
              <Input
                id="pa-vorb"
                type="number"
                value={form.vorbereitungszeitTage}
                onChange={(e) => setForm({ ...form, vorbereitungszeitTage: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="pa-notizen" className="block text-sm text-slate-700">
              Notizen
            </label>
            <Textarea
              id="pa-notizen"
              rows={3}
              value={form.notizen}
              onChange={(e) => setForm({ ...form, notizen: e.target.value })}
            />
          </div>
          <SheetFooter>
            {!istNeu && (
              <Button
                type="button"
                variant="destructive"
                disabled={loeschen.isPending}
                onClick={() => loeschen.mutate()}
              >
                Löschen
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              {istNeu ? "Prüfungsart anlegen" : "Speichern"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Tab: Wartungsarten (02)
// ---------------------------------------------------------------------------

type WartungsartForm = Record<
  | "titel"
  | "gueltigkeitsdauerJahre"
  | "gesetzlicheGrundlage"
  | "artDerTaetigkeit"
  | "extern"
  | "zustaendigeStelle"
  | "geplanteDauerStunden"
  | "anzahlMitarbeiter"
  | "vorbereitungszeitTage",
  string
>

const WARTUNGSART_LEER: WartungsartForm = {
  titel: "",
  gueltigkeitsdauerJahre: "",
  gesetzlicheGrundlage: "",
  artDerTaetigkeit: "",
  extern: "",
  zustaendigeStelle: "",
  geplanteDauerStunden: "",
  anzahlMitarbeiter: "",
  vorbereitungszeitTage: "",
}

function WartungsartenTab() {
  const queryClient = useQueryClient()
  const [sheet, setSheet] = useState<"neu" | Wartungsart | null>(null)
  const { data: arten } = useQuery({ queryKey: ["wartungsarten"], queryFn: fetchWartungsarten })

  const spalten = useMemo<ColumnDef<Wartungsart>[]>(
    () => [
      {
        id: "titel",
        header: "Titel",
        accessorFn: (a) => a.titel,
        cell: ({ row }) => <span className="font-medium text-slate-900">{row.original.titel}</span>,
      },
      {
        id: "gueltigkeit",
        header: "Gültigkeit (Jahre)",
        accessorFn: (a) => a.gueltigkeitsdauerJahre ?? -1,
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.gueltigkeitsdauerJahre ?? "—"}</span>
        ),
      },
      {
        id: "grundlage",
        header: "Gesetzl. Grundlage",
        accessorFn: (a) => a.gesetzlicheGrundlage ?? "",
        cell: ({ row }) => row.original.gesetzlicheGrundlage ?? "—",
      },
      {
        id: "taetigkeit",
        header: "Art der Tätigkeit",
        accessorFn: (a) => a.artDerTaetigkeit ?? "",
        cell: ({ row }) => row.original.artDerTaetigkeit ?? "—",
      },
      {
        id: "extern",
        header: "Extern",
        accessorFn: (a) => a.extern ?? "",
        cell: ({ row }) => row.original.extern ?? "—",
      },
      {
        id: "stelle",
        header: "Zuständige Stelle",
        accessorFn: (a) => a.zustaendigeStelle ?? "",
        cell: ({ row }) => row.original.zustaendigeStelle ?? "—",
      },
    ],
    [],
  )

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Katalog der Wartungstätigkeiten — die Gültigkeitsdauer bestimmt das Ablaufdatum der
          Wartung. Löschen ist nur möglich, solange keine Wartungen auf die Art verweisen.
        </p>
        <Button type="button" className="shrink-0" onClick={() => setSheet("neu")}>
          + Neue Wartungsart
        </Button>
      </div>
      <DataGrid
        columns={spalten}
        data={arten ?? []}
        storageKey="stammdaten-wartungsarten"
        emptyMessage="Noch keine Wartungsarten erfasst."
        onRowClick={(a) => setSheet(a)}
      />
      <WartungsartSheet
        zustand={sheet}
        onOpenChange={(open) => !open && setSheet(null)}
        onGeaendert={() => queryClient.invalidateQueries({ queryKey: ["wartungsarten"] })}
      />
    </section>
  )
}

function WartungsartSheet({
  zustand,
  onOpenChange,
  onGeaendert,
}: {
  zustand: "neu" | Wartungsart | null
  onOpenChange: (open: boolean) => void
  onGeaendert: () => void
}) {
  const istNeu = zustand === "neu"
  const art = istNeu || zustand === null ? null : zustand
  const toast = useToast()
  const [form, setForm] = useState<WartungsartForm>(WARTUNGSART_LEER)

  useEffect(() => {
    if (zustand === "neu") setForm(WARTUNGSART_LEER)
    else if (zustand)
      setForm({
        titel: zustand.titel,
        gueltigkeitsdauerJahre: zustand.gueltigkeitsdauerJahre?.toString() ?? "",
        gesetzlicheGrundlage: zustand.gesetzlicheGrundlage ?? "",
        artDerTaetigkeit: zustand.artDerTaetigkeit ?? "",
        extern: zustand.extern ?? "",
        zustaendigeStelle: zustand.zustaendigeStelle ?? "",
        geplanteDauerStunden: zustand.geplanteDauerStunden?.toString() ?? "",
        anzahlMitarbeiter: zustand.anzahlMitarbeiter?.toString() ?? "",
        vorbereitungszeitTage: zustand.vorbereitungszeitTage?.toString() ?? "",
      })
  }, [zustand])

  const zahl = (wert: string): number | null => (wert.trim() === "" ? null : Number(wert))
  const text = (wert: string): string | null => wert.trim() || null

  const speichern = useMutation({
    mutationFn: () => {
      const payload = {
        titel: form.titel.trim(),
        gueltigkeitsdauerJahre: zahl(form.gueltigkeitsdauerJahre),
        gesetzlicheGrundlage: text(form.gesetzlicheGrundlage),
        artDerTaetigkeit: text(form.artDerTaetigkeit),
        extern: text(form.extern),
        anbieterOrganisationId: art?.anbieterOrganisationId ?? null,
        zustaendigeStelle: text(form.zustaendigeStelle),
        geplanteDauerStunden: zahl(form.geplanteDauerStunden),
        anzahlMitarbeiter: zahl(form.anzahlMitarbeiter),
        vorbereitungszeitTage: zahl(form.vorbereitungszeitTage),
      }
      return istNeu ? createWartungsart(payload) : updateWartungsart(art!.id, payload)
    },
    onSuccess: () => {
      toast.success(istNeu ? "Wartungsart angelegt." : "Wartungsart gespeichert.")
      onGeaendert()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Wartungsart konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deleteWartungsart(art!.id),
    onSuccess: () => {
      toast.success("Wartungsart gelöscht.")
      onGeaendert()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(
        fehlerNachricht(err, "Löschen fehlgeschlagen — wird die Art noch von Wartungen genutzt?"),
      ),
  })

  return (
    <Sheet open={zustand !== null} onOpenChange={onOpenChange}>
      <SheetContent
        className="overflow-y-auto"
        sheetKey="stammdaten-wartungsart-form"
        defaultWidth={SHEET_WIDTH.md}
      >
        <SheetHeader>
          <SheetTitle>{istNeu ? "Neue Wartungsart" : `Wartungsart „${art?.titel}“`}</SheetTitle>
          <SheetDescription>
            Katalog der Wartungstätigkeiten (02) — Gültigkeitsdauer bestimmt das Ablaufdatum.
          </SheetDescription>
        </SheetHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (form.titel.trim()) speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="wa-titel" className="block text-sm text-slate-700">
              Titel <span className="text-red-600">*</span>
            </label>
            <Input
              id="wa-titel"
              required
              value={form.titel}
              onChange={(e) => setForm({ ...form, titel: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="wa-gueltigkeit" className="block text-sm text-slate-700">
                Gültigkeitsdauer (Jahre)
              </label>
              <Input
                id="wa-gueltigkeit"
                type="number"
                step="0.5"
                value={form.gueltigkeitsdauerJahre}
                onChange={(e) => setForm({ ...form, gueltigkeitsdauerJahre: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="wa-grundlage" className="block text-sm text-slate-700">
                Gesetzliche Grundlage
              </label>
              <Input
                id="wa-grundlage"
                value={form.gesetzlicheGrundlage}
                onChange={(e) => setForm({ ...form, gesetzlicheGrundlage: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="wa-taetigkeit" className="block text-sm text-slate-700">
                Art der Tätigkeit
              </label>
              <Input
                id="wa-taetigkeit"
                value={form.artDerTaetigkeit}
                onChange={(e) => setForm({ ...form, artDerTaetigkeit: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="wa-extern" className="block text-sm text-slate-700">
                Extern
              </label>
              <Input
                id="wa-extern"
                value={form.extern}
                onChange={(e) => setForm({ ...form, extern: e.target.value })}
                placeholder="z. B. Fremdfirma XY"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="wa-stelle" className="block text-sm text-slate-700">
              Zuständige Stelle
            </label>
            <Input
              id="wa-stelle"
              value={form.zustaendigeStelle}
              onChange={(e) => setForm({ ...form, zustaendigeStelle: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="wa-dauer" className="block text-sm text-slate-700">
                Dauer (h)
              </label>
              <Input
                id="wa-dauer"
                type="number"
                step="0.5"
                value={form.geplanteDauerStunden}
                onChange={(e) => setForm({ ...form, geplanteDauerStunden: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="wa-ma" className="block text-sm text-slate-700">
                Mitarbeiter
              </label>
              <Input
                id="wa-ma"
                type="number"
                value={form.anzahlMitarbeiter}
                onChange={(e) => setForm({ ...form, anzahlMitarbeiter: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="wa-vorb" className="block text-sm text-slate-700">
                Vorbereitung (Tage)
              </label>
              <Input
                id="wa-vorb"
                type="number"
                value={form.vorbereitungszeitTage}
                onChange={(e) => setForm({ ...form, vorbereitungszeitTage: e.target.value })}
              />
            </div>
          </div>
          <SheetFooter>
            {!istNeu && (
              <Button
                type="button"
                variant="destructive"
                disabled={loeschen.isPending}
                onClick={() => loeschen.mutate()}
              >
                Löschen
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              {istNeu ? "Wartungsart anlegen" : "Speichern"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
